import React, { useState, useEffect } from 'react';
import HuntersBoard from '../components/HuntersBoard';
import NavBar from '../components/NavBar';
import { auth } from '../lib/firebase';
import { useSystemSettings } from '../lib/SystemSettingsContext';
import { getInvitationsForUser, acceptBoardInvite, BoardInvitation } from '../lib/huntersBoardService';

const HuntersBoardPage: React.FC = () => {
  const [userName, setUserName] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [inviteCount, setInviteCount] = useState(0);
  const [invites, setInvites] = useState<BoardInvitation[]>([]);
  const [showInviteDropdown, setShowInviteDropdown] = useState(false);
  const [showInvitesDropdown, setShowInvitesDropdown] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [boardsRefreshKey, setBoardsRefreshKey] = useState(0);
  const { settings } = useSystemSettings();
  type InviteHelpers = {
    sendInvite: (email: string) => Promise<{ success: boolean; error?: string }>;
    getActiveBoardSummary: () => {
      boardId: string | null;
      boardName: string | null;
      pendingInvites: string[];
    };
  };

  const [inviteHelpers, setInviteHelpers] = useState<InviteHelpers | null>(null);
  const currentBoardSummary = inviteHelpers?.getActiveBoardSummary();

  useEffect(() => {
    const storedName = localStorage.getItem('rawpa_user_name');
    if (storedName) setUserName(storedName);
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        setUserId(user.uid);
        const displayName = user.displayName || user.email?.split('@')[0] || 'User';
        setUserName(displayName);
        localStorage.setItem('rawpa_user_name', displayName);
      } else {
        setUserId(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch invites
  useEffect(() => {
    if (!userId) return;
    getInvitationsForUser(userId).then(invites => {
      setInvites(invites);
      setInviteCount(invites.length);
    });
  }, [userId]);

  // When opening the pending invites dropdown, fetch invites
  useEffect(() => {
    if (showInvitesDropdown && userId) {
      getInvitationsForUser(userId).then(invites => {
        setInvites(invites);
        setInviteCount(invites.length);
      });
    }
  }, [showInvitesDropdown, userId]);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      window.location.href = '/signin';
    } catch (error) {
      alert('Failed to sign out. Please try again.');
    }
  };

  const handleAcceptInvite = async (invitationId: string) => {
    await acceptBoardInvite(invitationId);
    // Refresh invites
    if (userId) {
      const updated = await getInvitationsForUser(userId);
      setInvites(updated);
      setInviteCount(updated.length);
    }
    setBoardsRefreshKey(k => k + 1); // Trigger board refresh
  };

  const handleSendInvite = async () => {
    if (!inviteHelpers) {
      setInviteStatus('error');
      setInviteError('Board is not ready yet.');
      return;
    }

    const summary = inviteHelpers.getActiveBoardSummary();
    if (!summary.boardId) {
      setInviteStatus('error');
      setInviteError('Select a board before sending invitations.');
      return;
    }

    if (!inviteEmail) {
      setInviteStatus('error');
      setInviteError('Enter an email address to send an invite.');
      return;
    }

    setInviteStatus('sending');
    setInviteError(null);

    const result = await inviteHelpers.sendInvite(inviteEmail);

    if (result.success) {
      setInviteStatus('success');
      setInviteEmail('');
      setTimeout(() => setInviteStatus('idle'), 3000);
    } else {
      setInviteStatus('error');
      setInviteError(result.error || 'Failed to send invite');
    }
  };

  // On close of either dropdown, reset state
  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar with board actions dropdown */}
      <NavBar
        userName={userName}
        onSignOut={handleSignOut}
        inviteCount={inviteCount}
        onInviteUserClick={() => setShowInviteDropdown(true)}
        onShowInvitesClick={() => setShowInvitesDropdown(true)}
      />
      {/* Invitation Dropdown */}
      {showInvitesDropdown && (
        <div className="fixed top-24 right-8 z-50 w-80 bg-white rounded-lg shadow-lg border p-4">
          <h2 className="text-lg font-bold mb-4">Pending Board Invitations</h2>
          {invites.length === 0 ? (
            <div className="text-gray-500">No pending invitations.</div>
          ) : (
            <ul className="space-y-3">
              {invites.map(invite => (
                <li key={invite.id} className="flex items-center justify-between border-b pb-2">
                  <div>
                    <div className="font-semibold">Board: {invite.boardId}</div>
                    <div className="text-xs text-gray-500">Invited by: {invite.fromUserId}</div>
                  </div>
                  <button
                    className="btn btn-xs btn-primary"
                    onClick={() => handleAcceptInvite(invite.id)}
                  >
                    Accept
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button className="btn btn-xs mt-4" onClick={() => setShowInvitesDropdown(false)}>Close</button>
        </div>
      )}
      {/* Invite User Dropdown */}
      {showInviteDropdown && (
        <div className="fixed top-24 right-8 z-50 w-80 bg-white rounded-lg shadow-lg border p-4">
          <h2 className="text-lg font-bold mb-4">Invite User to Board</h2>
          <div className="text-xs text-gray-500 mb-2">
            {currentBoardSummary?.boardName
              ? `Active board: ${currentBoardSummary.boardName}`
              : 'Select a board in Hunter’s Board before inviting.'}
          </div>
          <input
            type="email"
            className="input input-bordered w-full mb-2"
            placeholder="Enter user email"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            disabled={inviteStatus === 'sending'}
          />
          <button
            className="btn btn-primary btn-xs w-full"
            onClick={handleSendInvite}
            disabled={inviteStatus === 'sending' || !inviteEmail}
          >
            {inviteStatus === 'sending' ? 'Sending...' : 'Send Invite'}
          </button>
          {inviteStatus === 'success' && <div className="text-green-600 mt-2">Invite sent!</div>}
          {inviteStatus === 'error' && <div className="text-red-600 mt-2">{inviteError}</div>}
          <button className="btn btn-xs mt-4" onClick={() => { setShowInviteDropdown(false); setInviteStatus('idle'); setInviteEmail(''); setInviteError(null); }}>Close</button>
        </div>
      )}
      <div className="container mx-auto px-4 pt-[160px]">
        <div className="bg-white rounded-[20px] p-8 shadow-lg w-full max-w-full overflow-x-auto">
          <div className="min-w-[900px] max-w-full">
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-6">Hunter's Board (Pentest Workspace)</h1>
            {settings.disableHuntersBoard ? (
              <div className="text-center text-lg text-gray-500 py-12">Under development, check again later.</div>
            ) : (
              <HuntersBoard
                boardsRefreshKey={boardsRefreshKey}
                onInviteHelpersChange={setInviteHelpers}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HuntersBoardPage; 